/**
 * MobileDevizPreview — Vizualizare simplificată a unui deviz pentru mobil.
 *
 * Înlocuiește generarea greoaie de PDF cu jsPDF + iframe (care îngheța telefoanele)
 * cu o vizualizare React nativă: carduri pe articole, info în secțiuni clare,
 * totale evidențiate. Pe desktop continuăm să folosim PDF iframe.
 */
import { FileText, Building2, User, MapPin, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";

const fmt = (n, isEUR = false, rate = 1) => {
  const v = Number(n) || 0;
  const converted = isEUR && rate > 0 ? v / rate : v;
  return converted.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function MobileDevizPreview({ devizInfo, workItems, totals, onDownloadPDF, currencyCode = "RON", exchangeRate = 1, isEUR = false }) {
  const f = (n) => fmt(n, isEUR, exchangeRate);
  return (
    <div className="p-3 sm:p-5 bg-white min-h-full" data-testid="mobile-deviz-preview">
      {/* Header */}
      <div className="text-center pb-3 mb-4 border-b-2 border-slate-800">
        <h1 className="text-lg sm:text-xl font-bold tracking-wide text-slate-900">DEVIZ OFERTĂ</h1>
        <p className="text-sm text-slate-600 mt-1">
          <strong>{devizInfo.numarDeviz}</strong> · {devizInfo.dataDeviz}
        </p>
        {isEUR && (
          <p className="text-[10px] text-amber-700 mt-1">Afișare în EUR · curs 1 EUR = {exchangeRate} RON</p>
        )}
      </div>

      {/* Info Cards */}
      <div className="space-y-2 mb-4">
        {devizInfo.executant && (
          <InfoLine icon={Building2} label="Executant" value={devizInfo.executant} sub={devizInfo.executantCui} />
        )}
        {devizInfo.beneficiar && (
          <InfoLine icon={User} label="Beneficiar" value={devizInfo.beneficiar} sub={devizInfo.beneficiarCui} />
        )}
        {devizInfo.obiectiv && (
          <InfoLine icon={ClipboardList} label="Obiectiv" value={devizInfo.obiectiv} />
        )}
        {devizInfo.adresa && (
          <InfoLine icon={MapPin} label="Adresă" value={devizInfo.adresa} />
        )}
      </div>

      {/* Work Items */}
      <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2 mt-4 pb-1 border-b">
        Lucrări ({workItems.length})
      </h2>
      <div className="space-y-2">
        {workItems.map((it, i) => {
          const qty = Number(it.quantity) || 0;
          const mat = Number(it.materialPrice) || 0;
          const man = Number(it.manoperaPrice) || 0;
          const subtotal = qty * (mat + man);
          return (
            <div
              key={it.id || i}
              className="bg-slate-50 border border-slate-200 rounded-lg p-2.5"
              data-testid={`mdp-item-${i}`}
            >
              <div className="flex items-start gap-2">
                <span className="font-mono text-[10px] bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded shrink-0">
                  {it.code || `#${i + 1}`}
                </span>
                <span className="text-xs sm:text-sm font-medium text-slate-900 flex-1 break-words">
                  {it.name || "Fără denumire"}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1 mt-2 text-[10px] sm:text-xs">
                <div className="text-center bg-white border rounded py-1">
                  <div className="text-slate-400">Cant.</div>
                  <div className="font-semibold tabular-nums">{Number(qty).toLocaleString("ro-RO", {maximumFractionDigits: 2})} {it.um}</div>
                </div>
                <div className="text-center bg-white border rounded py-1">
                  <div className="text-slate-400">Mat.</div>
                  <div className="font-semibold tabular-nums">{f(mat)}</div>
                </div>
                <div className="text-center bg-white border rounded py-1">
                  <div className="text-slate-400">Manoperă</div>
                  <div className="font-semibold tabular-nums">{f(man)}</div>
                </div>
                <div className="text-center bg-teal-50 border border-teal-200 rounded py-1">
                  <div className="text-teal-700">Total</div>
                  <div className="font-bold tabular-nums text-teal-900">{f(subtotal)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Totals */}
      <div className="mt-5 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-lg p-3 sm:p-4">
        <div className="space-y-1.5 text-sm">
          <Row label="Material" value={`${f(totals?.totalMaterial)} ${currencyCode}`} />
          <Row label="Manoperă" value={`${f(totals?.totalManopera)} ${currencyCode}`} />
          <Row label="Contrib. muncă" value={`${f(totals?.contributieMunca)} ${currencyCode}`} muted />
          <Row label="Indirecte" value={`${f((totals?.cheltuieliIndirecteMaterial || 0) + (totals?.cheltuieliIndirecteManopera || 0))} ${currencyCode}`} muted />
          <Row label="Profit" value={`${f((totals?.profitMaterial || 0) + (totals?.profitManopera || 0))} ${currencyCode}`} muted />
          <div className="border-t border-white/20 pt-1.5 mt-1.5">
            <Row label="Total fără TVA" value={`${f(totals?.totalFaraTVA)} ${currencyCode}`} />
            <Row label="TVA" value={`${f(totals?.tvaValue)} ${currencyCode}`} muted />
          </div>
          <div className="border-t-2 border-amber-400 pt-2 mt-1">
            <Row
              label="TOTAL CU TVA"
              value={`${f(totals?.totalCuTVA)} ${currencyCode}`}
              big
            />
          </div>
        </div>
      </div>

      {/* Observații */}
      {devizInfo.observatii && (
        <div className="mt-4 bg-amber-50 border-l-4 border-amber-400 p-3 text-xs sm:text-sm text-slate-800">
          <strong className="block mb-1">Observații:</strong>
          <p className="whitespace-pre-wrap">{devizInfo.observatii}</p>
        </div>
      )}

      {/* Download PDF on mobile */}
      {onDownloadPDF && (
        <div className="mt-5 sm:hidden">
          <Button
            onClick={onDownloadPDF}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            data-testid="mdp-download-pdf"
          >
            <FileText className="w-4 h-4 mr-2" /> Descarcă PDF complet
          </Button>
          <p className="text-[10px] text-slate-500 text-center mt-1.5">
            PDF-ul detaliat poate fi distribuit clientului.
          </p>
        </div>
      )}
    </div>
  );
}

const InfoLine = ({ icon: Icon, label, value, sub }) => (
  <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-2">
    <Icon className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase text-slate-500 tracking-wide">{label}</div>
      <div className="text-sm text-slate-900 break-words">{value}</div>
      {sub && <div className="text-xs text-slate-500 break-all">{sub}</div>}
    </div>
  </div>
);

const Row = ({ label, value, muted, big }) => (
  <div className={`flex items-center justify-between gap-2 ${muted ? "opacity-75 text-xs" : ""} ${big ? "text-lg sm:text-xl font-bold" : ""}`}>
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);
